import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { getProductCategoriesV1ProductCategoriesGet } from '../../generated';
import type { GetProductCategoriesV1ProductCategoriesGetData } from '../../generated';
import type { GetProductCategoriesV1ProductCategoriesGetError } from '../../generated';
import type { GetProductCategoriesV1ProductCategoriesGetResponse } from '../../generated';
import { extractTotalCount } from '../../utils';

export type GetProductCategoriesQueryVariables = NonNullable<
  GetProductCategoriesV1ProductCategoriesGetData['query']
> & {
  organization_id?: string;
  workspace_id?: string;
  retailer_region?: string;
};

export type GetProductCategoriesQuery = {
  data: GetProductCategoriesV1ProductCategoriesGetResponse;
  total_count: number;
};

export const useGetProductCategoriesQuery = <
  TData = GetProductCategoriesQuery,
  TError = GetProductCategoriesV1ProductCategoriesGetError,
>(
  {
    organization_id,
    workspace_id,
    breadcrumbs_like,
    limit,
    name_like,
    offset,
    sort_by,
    sort_direction,
    l1_category_id,
    category_ids,
    ids,
    scope_to_ICP,
    scope_to_portfolio,
    retailer_region,
    scorable_for_benchmark_id,
    scorable_for_retailer,
    scope,
  }: GetProductCategoriesQueryVariables,
  // The key and fetcher are the wrapper's; callers only tune behavior such as `enabled`.
  options?: Omit<UseQueryOptions<GetProductCategoriesQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetProductCategoriesQuery, TError, TData>({
    queryKey: [
      'GetProductCategories',
      {
        organization_id,
        workspace_id,
        breadcrumbs_like,
        limit,
        name_like,
        offset,
        sort_by,
        sort_direction,
        l1_category_id,
        category_ids,
        ids,
        scope_to_ICP,
        scope_to_portfolio,
        retailer_region,
        scorable_for_benchmark_id,
        scorable_for_retailer,
        scope,
      },
    ],
    queryFn: async () => {
      const { data, response } = await getProductCategoriesV1ProductCategoriesGet({
        query: {
          breadcrumbs_like,
          limit,
          name_like,
          offset,
          sort_by,
          sort_direction,
          l1_category_id,
          category_ids,
          ids,
          scope_to_ICP,
          scope_to_portfolio,
          retailer_region,
          scorable_for_benchmark_id,
          scorable_for_retailer,
          scope,
        },
        throwOnError: true,
      });
      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    ...options,
  });
};

useGetProductCategoriesQuery.getKey = (variables: GetProductCategoriesQueryVariables & { organizationId: string }) => [
  'GetProductCategories',
  variables,
];

useGetProductCategoriesQuery.fetcher = async (
  {
    breadcrumbs_like,
    limit,
    name_like,
    offset,
    sort_by,
    sort_direction,
    l1_category_id,
    category_ids,
    ids,
    scope_to_ICP,
    scope_to_portfolio,
    retailer_region,
    scorable_for_benchmark_id,
    scorable_for_retailer,
    scope,
  }: GetProductCategoriesQueryVariables,
  _headers?: RequestInit['headers'],
): Promise<GetProductCategoriesQuery> => {
  const { data, response } = await getProductCategoriesV1ProductCategoriesGet({
    query: {
      breadcrumbs_like,
      limit,
      name_like,
      offset,
      sort_by,
      sort_direction,
      l1_category_id,
      category_ids,
      ids,
      scope_to_ICP,
      scope_to_portfolio,
      retailer_region,
      scorable_for_benchmark_id,
      scorable_for_retailer,
      scope,
    },
    throwOnError: true,
  });
  return {
    data: data ?? [],
    total_count: extractTotalCount(response),
  };
};
